import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Award, 
  Star, 
  Users, 
  Crown, 
  Zap, 
  TrendingUp, 
  Target, 
  DollarSign,
  Rocket,
  Medal,
  Flame,
  Trophy,
  Gift,
  Loader2,
  Lock
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import { badgeAPI } from "../lib/api";

// Icon mapping
const BADGE_ICONS = {
  "user-plus": Users,
  "users": Users,
  "star": Star,
  "crown": Crown,
  "award": Award,
  "zap": Zap,
  "trending-up": TrendingUp,
  "target": Target,
  "dollar-sign": DollarSign,
  "banknote": DollarSign,
  "gem": Star,
  "trophy": Trophy,
  "rocket": Rocket,
  "medal": Medal,
  "flame": Flame,
  "gift": Gift,
};

export default function BadgesDisplay({ compact = false }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingBadge, setClaimingBadge] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await badgeAPI.getProgress();
      setProgress(response.data);
    } catch (error) {
      console.error("Failed to fetch badge progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (badgeId) => {
    setClaimingBadge(badgeId);
    try {
      const response = await badgeAPI.claimReward(badgeId);
      toast.success(`Claimed ${response.data.reward_value} ${response.data.reward_type}!`);
      fetchProgress(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim reward");
    } finally {
      setClaimingBadge(null);
    }
  };

  const getIcon = (iconName) => {
    return BADGE_ICONS[iconName] || Award;
  };

  if (loading) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!progress) return null;

  const earnedBadges = progress.badges?.filter(b => b.earned) || [];
  const inProgressBadges = progress.badges?.filter(b => !b.earned && b.progress > 0) || [];
  const lockedBadges = progress.badges?.filter(b => !b.earned && b.progress === 0) || [];

  if (compact) {
    // Compact view - just show earned badges count and next badge
    const nextBadge = inProgressBadges[0];
    
    return (
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-400" />
            Badges
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Earned</span>
            <span className="text-white font-bold">{earnedBadges.length}/{progress.total_badges}</span>
          </div>
          
          {/* Show earned badges as small icons */}
          {earnedBadges.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {earnedBadges.slice(0, 6).map((badge) => {
                const Icon = getIcon(badge.icon);
                return (
                  <div
                    key={badge.id}
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${badge.color}20` }}
                    title={badge.name}
                  >
                    <Icon className="w-4 h-4" style={{ color: badge.color }} />
                  </div>
                );
              })}
              {earnedBadges.length > 6 && (
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs text-muted-foreground">
                  +{earnedBadges.length - 6}
                </div>
              )}
            </div>
          )}

          {/* Next badge progress */}
          {nextBadge && (
            <div className="p-3 rounded-lg bg-secondary/30 border border-border/30">
              <div className="flex items-center gap-2 mb-2">
                {(() => {
                  const Icon = getIcon(nextBadge.icon);
                  return <Icon className="w-4 h-4" style={{ color: nextBadge.color }} />;
                })()}
                <span className="text-sm text-white">{nextBadge.name}</span>
              </div>
              <Progress value={nextBadge.progress} className="h-1.5" />
              <p className="text-xs text-muted-foreground mt-1">
                {nextBadge.current}/{nextBadge.target} - {Math.round(nextBadge.progress)}% complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">Your Badges</h3>
          <p className="text-muted-foreground">
            {earnedBadges.length} of {progress.total_badges} badges earned
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-400" />
          <span className="text-2xl font-bold text-white">{earnedBadges.length}</span>
        </div>
      </div>

      {/* Earned Badges */}
      {earnedBadges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Earned Badges</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {earnedBadges.map((badge) => {
              const Icon = getIcon(badge.icon);
              const canClaim = badge.reward && !badge.reward_claimed;
              
              return (
                <motion.div
                  key={badge.id}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="p-4 rounded-xl border-2 text-center"
                  style={{ 
                    backgroundColor: `${badge.color}10`,
                    borderColor: `${badge.color}50`
                  }}
                >
                  <div 
                    className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: `${badge.color}30` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: badge.color }} />
                  </div>
                  <h5 className="font-medium text-white text-sm">{badge.name}</h5>
                  <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                  
                  {canClaim && (
                    <Button
                      size="sm"
                      className="mt-2 w-full"
                      style={{ backgroundColor: badge.color }}
                      onClick={() => handleClaimReward(badge.id)}
                      disabled={claimingBadge === badge.id}
                    >
                      {claimingBadge === badge.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Gift className="w-3 h-3 mr-1" />
                          Claim +{badge.reward.value}
                        </>
                      )}
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* In Progress */}
      {inProgressBadges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">In Progress</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {inProgressBadges.map((badge) => {
              const Icon = getIcon(badge.icon);
              
              return (
                <div
                  key={badge.id}
                  className="p-4 rounded-xl bg-secondary/30 border border-border/30"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${badge.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: badge.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-white">{badge.name}</h5>
                      <p className="text-xs text-muted-foreground">{badge.description}</p>
                      <div className="mt-2">
                        <Progress value={badge.progress} className="h-1.5" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {badge.current}/{badge.target}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked Badges */}
      {lockedBadges.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Locked</h4>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {lockedBadges.map((badge) => {
              const Icon = getIcon(badge.icon);
              
              return (
                <div
                  key={badge.id}
                  className="p-3 rounded-xl bg-secondary/20 border border-border/20 text-center opacity-50"
                  title={badge.description}
                >
                  <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center bg-secondary/50 relative">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                    <Lock className="w-3 h-3 absolute -bottom-1 -right-1 text-muted-foreground" />
                  </div>
                  <h5 className="font-medium text-muted-foreground text-xs">{badge.name}</h5>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
